import { useState, useEffect } from "react";
import { intencionesAPI } from "@/lib/api";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, Edit, Trash2, CalendarDays, Clock, Check, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function IntencionesPage() {
  const [intenciones, setIntenciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingIntencion, setEditingIntencion] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterCelebrada, setFilterCelebrada] = useState("all");
  const [formData, setFormData] = useState({
    fecha_misa: "",
    hora_misa: "08:00",
    intencion: "",
    solicitante_nombre: "",
    solicitante_telefono: "",
    ofrenda: "",
    celebrada: false,
  });

  useEffect(() => {
    loadIntenciones();
  }, [filterCelebrada]);

  const loadIntenciones = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCelebrada === "pendiente") params.celebrada = false;
      if (filterCelebrada === "celebrada") params.celebrada = true;
      const response = await intencionesAPI.getAll(params);
      setIntenciones(response.data);
    } catch (error) {
      toast.error("Error al cargar las intenciones");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (intencion = null) => {
    if (intencion) {
      setEditingIntencion(intencion);
      setFormData({
        fecha_misa: intencion.fecha_misa,
        hora_misa: intencion.hora_misa,
        intencion: intencion.intencion,
        solicitante_nombre: intencion.solicitante_nombre,
        solicitante_telefono: intencion.solicitante_telefono || "",
        ofrenda: intencion.ofrenda?.toString() || "",
        celebrada: intencion.celebrada,
      });
    } else {
      setEditingIntencion(null);
      setFormData({
        fecha_misa: format(selectedDate, "yyyy-MM-dd"),
        hora_misa: "08:00",
        intencion: "",
        solicitante_nombre: "",
        solicitante_telefono: "",
        ofrenda: "",
        celebrada: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingIntencion(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      ofrenda: formData.ofrenda ? parseFloat(formData.ofrenda) : null,
    };

    try {
      if (editingIntencion) {
        await intencionesAPI.update(editingIntencion.id, data);
        toast.success("Intención actualizada correctamente");
      } else {
        await intencionesAPI.create(data);
        toast.success("Intención registrada correctamente");
      }
      handleCloseDialog();
      loadIntenciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar la intención");
    }
  };

  const handleMarcarCelebrada = async (id) => {
    try {
      await intencionesAPI.marcarCelebrada(id);
      toast.success("Intención marcada como celebrada");
      loadIntenciones();
    } catch (error) {
      toast.error("Error al actualizar la intención");
    }
  };

  const handleDelete = async () => {
    try {
      await intencionesAPI.delete(deleteId);
      toast.success("Intención eliminada correctamente");
      loadIntenciones();
    } catch (error) {
      toast.error("Error al eliminar la intención");
    } finally {
      setDeleteId(null);
    }
  };

  const handlePrintDay = () => {
    window.print();
  };

  const intencionesDelDia = intenciones.filter(
    (i) => i.fecha_misa === format(selectedDate, "yyyy-MM-dd")
  );

  const horasMisa = [
    "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00",
    "17:00", "18:00", "19:00", "20:00"
  ];

  return (
    <div className="space-y-6" data-testid="intenciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <CalendarDays className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            Intenciones de Misa
          </h1>
          <p className="text-stone-600 mt-1">Gestión de intenciones para las celebraciones</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handlePrintDay}
            variant="outline"
            className="h-10"
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Día
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-rose-950 hover:bg-rose-900 text-white"
            data-testid="nueva-intencion-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Intención
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="border-stone-200 bg-white no-print">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Calendario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              defaultMonth={new Date()}
              locale={es}
              className="rounded-md border border-stone-200"
            />
            <div className="mt-4">
              <Select value={filterCelebrada} onValueChange={setFilterCelebrada}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="celebrada">Celebradas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Intenciones del día */}
        <Card className="lg:col-span-2 border-stone-200 bg-white">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-stone-800">
                Intenciones para el {format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })}
              </CardTitle>
              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                {intencionesDelDia.length} intenciones
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-stone-400 animate-pulse">
                Cargando...
              </div>
            ) : intencionesDelDia.length === 0 ? (
              <div className="py-8 text-center text-stone-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay intenciones para este día</p>
                <Button
                  onClick={() => handleOpenDialog()}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar intención
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {intencionesDelDia
                  .sort((a, b) => a.hora_misa.localeCompare(b.hora_misa))
                  .map((intencion) => (
                    <div
                      key={intencion.id}
                      className={`p-4 rounded-lg border ${
                        intencion.celebrada
                          ? "bg-stone-50 border-stone-200"
                          : "bg-amber-50 border-amber-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge
                              variant="outline"
                              className={`${
                                intencion.celebrada
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-100 text-amber-800 border-amber-300"
                              }`}
                            >
                              <Clock className="w-3 h-3 mr-1" />
                              {intencion.hora_misa}
                            </Badge>
                            {intencion.celebrada && (
                              <Badge
                                variant="outline"
                                className="bg-emerald-50 text-emerald-700 border-emerald-200"
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Celebrada
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium text-stone-800">
                            {intencion.intencion}
                          </p>
                          <p className="text-sm text-stone-500 mt-1">
                            Solicitante: {intencion.solicitante_nombre}
                            {intencion.ofrenda && ` · Ofrenda: $${intencion.ofrenda}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 no-print">
                          {!intencion.celebrada && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleMarcarCelebrada(intencion.id)}
                              title="Marcar como celebrada"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenDialog(intencion)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteId(intencion.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingIntencion ? "Editar Intención" : "Nueva Intención"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_misa">Fecha *</Label>
                <Input
                  id="fecha_misa"
                  name="fecha_misa"
                  type="date"
                  value={formData.fecha_misa}
                  onChange={handleChange}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_misa">Hora *</Label>
                <Select
                  value={formData.hora_misa}
                  onValueChange={(value) => setFormData({ ...formData, hora_misa: value })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {horasMisa.map((hora) => (
                      <SelectItem key={hora} value={hora}>
                        {hora}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="intencion">Intención *</Label>
              <Textarea
                id="intencion"
                name="intencion"
                value={formData.intencion}
                onChange={handleChange}
                required
                rows={3}
                className="resize-none"
                placeholder="Por el eterno descanso de..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="solicitante_nombre">Nombre del Solicitante *</Label>
              <Input
                id="solicitante_nombre"
                name="solicitante_nombre"
                value={formData.solicitante_nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="solicitante_telefono">Teléfono</Label>
                <Input
                  id="solicitante_telefono"
                  name="solicitante_telefono"
                  value={formData.solicitante_telefono}
                  onChange={handleChange}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ofrenda">Ofrenda ($)</Label>
                <Input
                  id="ofrenda"
                  name="ofrenda"
                  type="number"
                  step="0.01"
                  value={formData.ofrenda}
                  onChange={handleChange}
                  className="h-11"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-rose-950 hover:bg-rose-900 text-white">
                {editingIntencion ? "Actualizar" : "Registrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta intención?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La intención será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
